/**
 * Inline SVG icons.
 *
 * Bundled as components rather than pulled from an icon package: the app needs
 * about a dozen glyphs, and inlining them avoids shipping a font or a
 * multi-thousand-icon dependency to a phone.
 */

interface IconProps {
  className?: string;
}

const base = {
  viewBox: '0 0 24 24',
  // An SVG with no intrinsic size falls back to 300x150 and then stretches to
  // fill a flex parent, which turns any button containing one into a giant
  // block. Every icon therefore carries a default size; CSS can still override
  // it (the bottom nav does).
  width: 20,
  height: 20,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

export function TodayIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <rect x="3" y="4" width="18" height="18" rx="3" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}

export function PlusIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

export function ScaleIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M12 3v4" />
      <path d="M5 7h14l2 11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <path d="M9 12a3 3 0 0 0 6 0" />
    </svg>
  );
}

export function SettingsIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 7 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0-1.2-2.9H1a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 2.6 7a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H7a1.7 1.7 0 0 0 1-1.5V1a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 2.9 1.2 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V7a1.7 1.7 0 0 0 1.5 1H23a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}

export function BarcodeIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M3 5v14M7 5v14M11 5v10M15 5v14M19 5v14" />
    </svg>
  );
}

export function CameraIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  );
}

export function TextIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M4 6h16M4 12h10M4 18h13" />
    </svg>
  );
}

export function SearchIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

export function ClockIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** A plate with cutlery — the meal-photo mode. */
export function PlateIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="3.5" />
      <path d="M2.5 4v4M21.5 4v4" />
    </svg>
  );
}

export function RecipeIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3Z" />
      <path d="M4 17h16" />
      <path d="M9 8h6M9 12h4" />
    </svg>
  );
}

/** Bar chart — the trends screen. */
export function ChartIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M3 3v18h18" />
      <path d="M7 15v3M12 10v8M17 6v12" />
    </svg>
  );
}

/** A droplet — water tracking. */
export function DropletIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M12 3s6 5.5 6 10a6 6 0 0 1-12 0c0-4.5 6-10 6-10Z" />
    </svg>
  );
}

/** Two stacked cards — copy a meal or day. */
export function CopyIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <rect x="9" y="9" width="12" height="12" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

/** A flame — the logging streak. */
export function FlameIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M12 3s5 4.5 5 9a5 5 0 0 1-10 0c0-1.6.7-3 1.5-4 .3 1.2 1 2 1.9 2.3C10.6 8.4 12 6 12 3Z" />
    </svg>
  );
}

export function TrashIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14" />
    </svg>
  );
}

export function CloseIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

export function ChevronLeftIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

export function ChevronRightIcon({ className }: IconProps): JSX.Element {
  return (
    <svg {...base} className={className}>
      <path d="m9 18 6-6-6-6" />
    </svg>
  );
}

/** The app mark: a leaf silhouette formed from a stylised "N". */
export function BrandMark({ className }: IconProps): JSX.Element {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
      <rect width="48" height="48" rx="12" fill="#16a34a" />
      <path
        d="M14 34V15c0-.6.7-.9 1.1-.5L33 32.5c.4.4 1.1.1 1.1-.5V14"
        fill="none"
        stroke="#ffffff"
        strokeWidth="4"
        strokeLinecap="round"
      />
      <circle cx="34" cy="34" r="3.5" fill="#bbf7d0" />
    </svg>
  );
}
